import {
  Button,
  Input,
  SelectSearch,
  Form,
} from "@telluscompany/design-system";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";
import { cleanEmptyStrings } from "@shared/hooks";

import type { AdminStructureFilterData } from "../../schemas";
import type {
  StructureOperationalStatus,
  StructureType,
} from "../../api/structures/structures.types";

interface AdminStructuresFilterProps {
  onFilter: (data: Partial<AdminStructureFilterData>) => void;
  onClear: () => void;
  initialFilters?: Partial<AdminStructureFilterData>;
}

const STRUCTURE_TYPES: StructureType[] = ["dam", "stockpile", "openPit"];
const OPERATIONAL_STATUSES: StructureOperationalStatus[] = [
  "decommissioned",
  "underConstruction",
  "inOperation",
  "underConstructionAndOperation",
  "underDecommissioning",
  "halted",
];

export const AdminStructuresFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: AdminStructuresFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<AdminStructureFilterData>({
    defaultValues: {
      name: initialFilters?.name ?? "",
      type: initialFilters?.type,
      operationalStatus: initialFilters?.operationalStatus,
    },
  });

  const typeOptions = useMemo(
    () =>
      STRUCTURE_TYPES.map((value) => ({
        value,
        label: translate(`admin.structures.structureType.${value}`),
      })),
    [translate],
  );

  const operationalStatusOptions = useMemo(
    () =>
      OPERATIONAL_STATUSES.map((value) => ({
        value,
        label: translate(`admin.structures.operationalStatus.${value}`),
      })),
    [translate],
  );

  const handleFilter = (data: AdminStructureFilterData) => {
    onFilter(cleanEmptyStrings(data));
  };

  const handleClear = () => {
    form.reset({ name: "", type: undefined, operationalStatus: undefined });
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <Input
            control={form.control}
            label={`${translate("admin.structures.filter.label.name")}:`}
            name="name"
            type="text"
            data-testid="structures-filter-name"
            placeholder={translate(
              "admin.structures.filter.placeholder.name",
            )}
          />
          <SelectSearch
            control={form.control}
            label={`${translate("admin.structures.filter.label.type")}:`}
            name="type"
            placeholder={translate(
              "admin.structures.filter.placeholder.type",
            )}
            options={typeOptions}
            clearable
          />
          <SelectSearch
            control={form.control}
            label={`${translate(
              "admin.structures.filter.label.operationalStatus",
            )}:`}
            name="operationalStatus"
            placeholder={translate(
              "admin.structures.filter.placeholder.operationalStatus",
            )}
            options={operationalStatusOptions}
            clearable
          />
        </div>
        <div className="flex justify-end mb-4 gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate("admin.structures.filter.button.clearFilters")}
          </Button>
          <Button
            type="submit"
            data-testid="structures-filter-submit"
            iconLeft={<Filter fill="currentColor" />}
          >
            {translate("admin.structures.filter.button.filter")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
